import { z } from '../zod';

// Booleans and nullable strings rather than literals and `null` types:
// swift-openapi-generator has no literal or null-only types.
export const LoginRequestSchema = z
  .object({ email: z.string(), password: z.string(), invite: z.string().optional() })
  .openapi('LoginRequest');

export const SignupRequestSchema = z
  .looseObject({
    email: z.string(),
    password: z.string(),
    name: z.string().optional(),
    marketingConsent: z.boolean().optional(),
    // The dashboard sends null for both when it has no tracking session or ad click.
    session: z.string().nullable().optional(),
    gclid: z.string().nullable().optional(),
    invite: z.string().optional(),
  })
  .openapi('SignupRequest');

// The login responses carry the whole user document; only these fields are promised.
export const SessionUserSchema = z
  .looseObject({
    _id: z.string(),
    email: z.string(),
    name: z.string().optional(),
    role: z.string().optional(),
  })
  .openapi('SessionUser');

export const LoginSuccessSchema = z
  .object({
    auth: z.boolean(),
    data: z.object({
      user: SessionUserSchema,
      token: z.string(),
      isNewUser: z.boolean().optional(),
      accountLinked: z.boolean().optional(),
      inviteAccepted: z.boolean().optional(),
      needsOnboarding: z.boolean().optional(),
    }),
  })
  .openapi('LoginSuccess');

export const LoginFailureSchema = z
  .object({ auth: z.boolean(), token: z.string().nullable(), message: z.string() })
  .openapi('LoginFailure');

export const AuthSessionQuerySchema = z.object({
  customerId: z.string().optional(),
  paymentId: z.string().optional(),
  subscriptionId: z.string().optional(),
  submissionId: z.string().optional(),
  bookingId: z.string().optional(),
  couponId: z.string().optional(),
  pageId: z.string().optional(),
});

export const AuthTokenSchema = z
  .object({ token: z.string(), expiresAt: z.string() })
  .openapi('AuthToken');

export const RevokeResultSchema = z.object({ revoked: z.boolean() }).openapi('RevokeResult');

export const GoogleAuthUrlRequestSchema = z
  .object({ redirectURI: z.string().optional(), state: z.string().nullable().optional() })
  .openapi('GoogleAuthUrlRequest');

export const GoogleAuthUrlSchema = z.object({ authUrl: z.string() }).openapi('GoogleAuthUrl');

export const GoogleLoginRequestSchema = z
  .object({
    code: z.string().optional(),
    redirectURI: z.string().optional(),
    marketingConsent: z.boolean().optional(),
    inviteToken: z.string().optional(),
    gclid: z.string().optional(),
    sessionId: z.string().optional(),
  })
  .openapi('GoogleLoginRequest');

export const GoogleAccountLinkingSchema = z
  .object({
    requiresAccountLinking: z.boolean(),
    email: z.string(),
    hasInvite: z.boolean(),
    linkToken: z.string(),
    message: z.string(),
  })
  .openapi('GoogleAccountLinking');

export const GoogleLoginResponseSchema = z
  .union([LoginSuccessSchema, GoogleAccountLinkingSchema])
  .openapi('GoogleLoginResponse');

export const GoogleLinkRequestSchema = z
  .object({
    linkToken: z.string(),
    email: z.string().optional(),
    password: z.string().optional(),
    inviteToken: z.string().optional(),
  })
  .openapi('GoogleLinkRequest');

export const GoogleAcceptInviteRequestSchema = z
  .object({
    code: z.string().optional(),
    redirectURI: z.string().optional(),
    inviteToken: z.string().optional(),
    marketingConsent: z.boolean().optional(),
  })
  .openapi('GoogleAcceptInviteRequest');

export const GoogleUnlinkResponseSchema = z
  .object({ user: SessionUserSchema, message: z.string() })
  .openapi('GoogleUnlinkResponse');

export type LoginRequest = z.infer<typeof LoginRequestSchema>;
export type LoginSuccess = z.infer<typeof LoginSuccessSchema>;
export type LoginFailure = z.infer<typeof LoginFailureSchema>;
export type SessionUser = z.infer<typeof SessionUserSchema>;
